import React, { useEffect, useState } from "react";
import api, { rupee, downloadFile, formatApiError } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Search, Download, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { OrderTimeline } from "../../components/OrderTimeline";

const STATUSES = ["placed", "accepted", "preparing", "ready", "out_for_delivery", "delivered", "cancelled", "stock_out"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [payment, setPayment] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [waLogs, setWaLogs] = useState([]);
  const [note, setNote] = useState("");

  const openDetail = async (o) => {
    setSelected(o); setWaLogs([]); setNote(o.admin_note || "");
    try { const { data } = await api.get(`/admin/orders/${o.id}/whatsapp`); setWaLogs(data); } catch {}
  };
  const resendWa = async (recipient_type) => {
    try {
      const { data } = await api.post(`/admin/orders/${selected.id}/whatsapp/resend`, null, { params: { recipient_type } });
      toast.success(`Resent — status: ${data.status}`);
      const { data: logs } = await api.get(`/admin/orders/${selected.id}/whatsapp`); setWaLogs(logs);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (payment !== "all") params.payment = payment;
    if (search) params.search = search;
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [status, payment, search]);

  const updateStatus = async (id, newStatus) => {
    await api.put(`/admin/orders/${id}/status`, { status: newStatus });
    toast.success("Order status updated");
    load();
    if (selected?.id === id) setSelected({ ...selected, status: newStatus });
  };

  const acceptOrder = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/acknowledge`);
      toast.success("Order accepted — moved to Running");
      load();
      if (selected?.id === id) setSelected({ ...selected, status: "accepted" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveNote = async () => {
    try {
      await api.put(`/admin/orders/${selected.id}/note`, { note });
      toast.success("Note saved for the kitchen");
      setSelected({ ...selected, admin_note: note });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-serif text-3xl text-navy">Orders</h1>
        <button onClick={async () => { try { await downloadFile("/admin/orders/export/xlsx", "orders.xlsx"); } catch { toast.error("Export failed"); } }} data-testid="export-orders-btn" className="flex items-center gap-2 bg-navy text-cream text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-navy-soft transition-colors"><Download size={15} /> Export Excel</button>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex items-center bg-white border border-black/10 rounded-lg px-3 py-2 flex-1 min-w-[220px]">
          <Search size={16} className="text-muted-foreground" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order # / name / phone" data-testid="order-search" className="bg-transparent outline-none text-sm ml-2 flex-1" />
        </div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-[170px] bg-white" data-testid="order-status-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
        <Select value={payment} onValueChange={setPayment}><SelectTrigger className="w-[150px] bg-white" data-testid="order-payment-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Payments</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="cod_pending">COD</SelectItem></SelectContent></Select>
      </div>

      <div className="bg-white rounded-xl border border-black/5 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-beige text-navy text-left"><tr><th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Amount</th><th className="p-3">Payment</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
          <tbody>
            {orders.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No orders found</td></tr> :
              orders.map((o) => (
                <tr key={o.id} className="border-t border-black/5 hover:bg-beige/40" data-testid={`admin-order-${o.order_number}`}>
                  <td className="p-3"><div className="font-medium text-navy">{o.order_number}</div><div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-IN")}</div></td>
                  <td className="p-3"><div className="text-navy">{o.customer.name}</div><div className="text-xs text-muted-foreground">{o.customer.phone}</div></td>
                  <td className="p-3 font-medium text-navy">{rupee(o.grand_total)}</td>
                  <td className="p-3"><span className="text-xs bg-beige px-2 py-1 rounded capitalize">{o.payment_status}</span></td>
                  <td className="p-3">
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                      <SelectTrigger className="w-[150px] h-8 text-xs" data-testid={`status-select-${o.order_number}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize text-xs">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3"><div className="flex items-center gap-2">
                    {o.status === "placed" && <button onClick={() => acceptOrder(o.id)} data-testid={`accept-order-${o.order_number}`} className="text-xs font-semibold text-cream bg-navy hover:bg-navy-soft rounded-full px-3 py-1.5">Accept</button>}
                    <button onClick={() => openDetail(o)} className="text-xs font-semibold text-navy hover:text-gold" data-testid={`view-order-${o.order_number}`}>Details</button>
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected && (<>
            <DialogHeader><DialogTitle className="font-serif text-2xl">{selected.order_number}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <OrderTimeline status={selected.status} />
              <div className="bg-beige rounded-lg p-3">
                <div className="font-medium text-navy">{selected.customer.name} · {selected.customer.phone}</div>
                {selected.order_type === "takeaway" && selected.kitchen ? (
                  <div className="text-muted-foreground text-xs mt-1">Takeaway · Pickup from {selected.kitchen.name}, {selected.kitchen.address} · Ready in {selected.kitchen.ready_time}</div>
                ) : selected.delivery_address ? (
                  <div className="text-muted-foreground text-xs mt-1">{selected.delivery_address.address}, {selected.delivery_address.city} - {selected.delivery_address.pincode}{selected.delivery_address.instructions ? ` · Note: ${selected.delivery_address.instructions}` : ""}</div>
                ) : null}
              </div>
              {selected.special_instructions && (
                <div className="bg-gold/10 border border-gold/30 rounded-lg p-3" data-testid="admin-special-instructions">
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-navy mb-1 flex items-center gap-1.5"><MessageSquare size={13} className="text-gold" /> Special Request</div>
                  <div className="text-sm text-navy">{selected.special_instructions}</div>
                </div>
              )}
              <div className="space-y-1">{selected.items.map((it, i) => <div key={i} className="flex justify-between"><span>{it.name} × {it.quantity}{it.variant ? ` (${it.variant})` : ""}</span><span>{rupee(it.total)}</span></div>)}</div>
              <div className="flex justify-between font-semibold text-navy border-t border-black/10 pt-2"><span>Total</span><span>{rupee(selected.grand_total)}</span></div>
              {selected.uengage && <div className="text-xs bg-navy text-cream rounded-lg p-3"><span className="text-gold font-semibold">UEngage:</span> {selected.uengage.status} — {selected.uengage.message}</div>}
              {selected.chef_remark && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3" data-testid="admin-chef-remark">
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-navy mb-1">Chef Remark</div>
                  <div className="text-sm text-navy">{selected.chef_remark}</div>
                </div>
              )}
              {selected.status === "placed" && (
                <button onClick={() => acceptOrder(selected.id)} data-testid="dialog-accept-order" className="w-full bg-navy text-cream font-semibold py-2.5 rounded-full hover:bg-navy-soft">Accept Order (move to Running)</button>
              )}
              <div className="border border-black/10 rounded-lg p-3">
                <div className="font-semibold text-navy text-xs uppercase tracking-wide mb-2">Admin Note to Kitchen</div>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note for the kitchen (e.g. a request the customer made after ordering)" data-testid="admin-order-note-input" rows={2} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
                <button onClick={saveNote} data-testid="save-admin-note-btn" className="mt-2 text-xs font-semibold text-navy border border-navy/20 rounded-full px-4 py-1.5 hover:bg-beige">Save Note</button>
              </div>
              <div className="border border-black/10 rounded-lg p-3" data-testid="order-whatsapp-panel">
                <div className="font-semibold text-navy text-xs uppercase tracking-wide mb-2">WhatsApp Notifications</div>
                {["CUSTOMER", "BUSINESS_ADMIN"].map((rt) => {
                  const log = waLogs.find((l) => l.recipient_type === rt);
                  const st = log?.status || "pending";
                  const color = st === "sent" || st === "delivered" ? "text-green-700" : st === "failed" ? "text-destructive" : "text-amber-700";
                  return (
                    <div key={rt} className="flex items-center justify-between py-1.5 border-b border-black/5 last:border-0">
                      <div>
                        <div className="text-xs font-medium text-navy">{rt === "CUSTOMER" ? "Customer" : "Business / Admin"}</div>
                        <div className="text-[11px] text-muted-foreground">{log ? `${log.recipient_number || "—"} · ${log.template_name || ""}${log.provider_message_id ? " · " + log.provider_message_id : ""}${log.error_message ? " · " + log.error_message : ""}` : "Not sent yet"}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[11px] font-semibold uppercase ${color}`} data-testid={`wa-status-${rt.toLowerCase()}`}>{st}</span>
                        <button onClick={() => resendWa(rt)} data-testid={`resend-wa-${rt.toLowerCase()}`} className="text-[11px] font-semibold text-navy hover:text-gold border border-navy/15 rounded-full px-2.5 py-1">Resend</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
